import React from 'react';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';

/**
 * ThemeSwitch - Toggle between light/dark/auto themes
 * Unified component for all devices
 */
const ThemeSwitch = ({ className, compact = false }) => {
  const { themeMode, isDark, toggleTheme, setTheme } = useTheme();
  
  const modes = [
    { id: 'light', icon: Sun, label: 'Jour' },
    { id: 'auto', icon: Monitor, label: 'Auto' },
    { id: 'dark', icon: Moon, label: 'Nuit' },
  ];
  
  if (compact) {
    // Simple toggle button
    return (
      <button
        onClick={toggleTheme}
        className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          "bg-gray-100 dark:bg-zinc-800 border-2 border-gray-200 dark:border-zinc-700",
          "hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors",
          className
        )}
        title={`Mode: ${themeMode === 'auto' ? 'Automatique' : themeMode === 'dark' ? 'Nuit' : 'Jour'}`}
      >
        {themeMode === 'auto' ? (
          <Monitor className="w-5 h-5 text-blue-500" />
        ) : isDark ? (
          <Moon className="w-5 h-5 text-indigo-400" />
        ) : (
          <Sun className="w-5 h-5 text-amber-500" />
        )}
      </button>
    );
  }
  
  // Full segmented control
  return (
    <div className={cn(
      "inline-flex p-1 rounded-xl bg-gray-100 dark:bg-zinc-800",
      "border-2 border-gray-200 dark:border-zinc-700",
      className
    )}>
      {modes.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          onClick={() => setTheme(id)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all",
            themeMode === id
              ? "bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          )}
        >
          <Icon className={cn(
            "w-4 h-4",
            themeMode === id && id === 'light' && "text-amber-500",
            themeMode === id && id === 'auto' && "text-blue-500",
            themeMode === id && id === 'dark' && "text-indigo-400"
          )} />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
};

export default ThemeSwitch;
